// src/utils/tournamentRanking.ts

import {
  TournamentState,
  TournamentRecord,
  Match,
  MatchResult,
} from './bucketingState';

/**
 * Transitivity graph: tracks which tasks beat which.
 * If graph[A][B] = true, then A > B (directly or transitively).
 */
type TransitiveGraph = Record<string, Record<string, boolean>>;

/**
 * Build transitive closure from direct comparisons.
 * Uses Floyd-Warshall algorithm to compute all implied relationships.
 */
function buildTransitiveClosure(
  tasks: string[],
  history: MatchResult[]
): TransitiveGraph {
  const graph: TransitiveGraph = {};

  // Initialize empty graph
  for (const task of tasks) {
    graph[task] = {};
  }

  // Add direct comparisons from history
  for (const result of history) {
    const { match } = result;
    if (match.winner && match.winner !== 'skip') {
      const loser = match.winner === match.taskA ? match.taskB : match.taskA;
      graph[match.winner][loser] = true;
    }
  }

  // Floyd-Warshall for transitive closure
  for (const k of tasks) {
    for (const i of tasks) {
      for (const j of tasks) {
        // If i > k and k > j, then i > j
        if (graph[i][k] && graph[k][j]) {
          graph[i][j] = true;
        }
      }
    }
  }

  return graph;
}

/**
 * Check if we already know the relationship between two tasks.
 * Returns: 'a' if A > B, 'b' if B > A, null if unknown.
 */
function getKnownResult(
  graph: TransitiveGraph,
  taskA: string,
  taskB: string
): 'a' | 'b' | null {
  if (graph[taskA]?.[taskB]) return 'a';
  if (graph[taskB]?.[taskA]) return 'b';
  return null;
}

/**
 * Initialize tournament state for a list of task IDs.
 * Uses transitivity and tier-based pairing to minimize comparisons.
 */
export function initializeTournament(taskIds: string[]): TournamentState {
  const n = taskIds.length;

  // IMPROVEMENT 1: Fewer rounds - max 4 for any size
  // With tier-based pairing and early termination, we need even fewer rounds
  const totalRounds = n <= 10 ? 2 : n <= 20 ? 3 : 4;

  // Initialize results for all tasks
  const results: Record<string, TournamentRecord> = {};
  for (const taskId of taskIds) {
    results[taskId] = { wins: 0, losses: 0, ties: 0 };
  }

  // Generate first round matches (empty graph initially)
  const emptyGraph: TransitiveGraph = {};
  for (const taskId of taskIds) {
    emptyGraph[taskId] = {};
  }
  const matches = generateSmartMatches(taskIds, results, emptyGraph, []);

  return {
    phase: 'tournament',
    tasks: [...taskIds],
    currentRound: 1,
    totalRounds,
    matches,
    currentMatchIndex: 0,
    results,
    eliminatedTasks: [],
    history: [],
  };
}

/**
 * IMPROVEMENT 3: Smart tier-based match generation
 * - Limits each task to max 3 total comparisons
 * - Prioritizes cross-tier comparisons over same-tier
 * - Uses transitivity to skip known results
 */
function generateSmartMatches(
  activeTasks: string[],
  results: Record<string, TournamentRecord>,
  graph: TransitiveGraph,
  history: MatchResult[]
): Match[] {
  const matches: Match[] = [];
  const paired = new Set<string>();

  // Count how many times each task has been compared
  const comparisonCount: Record<string, number> = {};
  for (const task of activeTasks) {
    comparisonCount[task] = 0;
  }
  for (const result of history) {
    if (activeTasks.includes(result.match.taskA)) {
      comparisonCount[result.match.taskA] = (comparisonCount[result.match.taskA] || 0) + 1;
    }
    if (activeTasks.includes(result.match.taskB)) {
      comparisonCount[result.match.taskB] = (comparisonCount[result.match.taskB] || 0) + 1;
    }
  }

  // Sort by record (wins - losses) for Swiss-style pairing
  const sorted = [...activeTasks].sort((a, b) => {
    const aScore = results[a].wins - results[a].losses;
    const bScore = results[b].wins - results[b].losses;
    if (bScore !== aScore) return bScore - aScore;
    // Secondary: fewer comparisons = higher priority
    return comparisonCount[a] - comparisonCount[b];
  });

  // Max comparisons per task (keeps total reasonable)
  const maxComparisons = 3;

  for (const taskA of sorted) {
    if (paired.has(taskA)) continue;
    if (comparisonCount[taskA] >= maxComparisons) continue;

    const scoreA = results[taskA].wins - results[taskA].losses;

    // Find best opponent - prefer different tier, accept same tier if needed
    let bestOpponent: string | null = null;

    for (const taskB of sorted) {
      if (taskB === taskA || paired.has(taskB)) continue;
      if (comparisonCount[taskB] >= maxComparisons) continue;

      // Skip if we already know the result via transitivity
      if (getKnownResult(graph, taskA, taskB) !== null) continue;

      const scoreB = results[taskB].wins - results[taskB].losses;
      const sameTier = scoreA === scoreB;

      // For same-tier, be more selective - only if both have few comparisons
      if (sameTier && comparisonCount[taskA] >= 2 && comparisonCount[taskB] >= 2) {
        continue;
      }

      bestOpponent = taskB;
      break;
    }

    if (bestOpponent) {
      matches.push({ taskA, taskB: bestOpponent, winner: null });
      paired.add(taskA);
      paired.add(bestOpponent);
    }
  }

  return matches;
}

/**
 * Record a match result.
 */
export function recordMatchResult(
  state: TournamentState,
  winner: string | 'skip'
): TournamentState {
  const match = state.matches[state.currentMatchIndex];
  if (!match) return state;

  const newResults = { ...state.results };
  const newMatch = { ...match };

  if (winner === 'skip') {
    // Tie: both get 0.5 points (tracked as ties)
    newResults[match.taskA] = {
      ...newResults[match.taskA],
      ties: newResults[match.taskA].ties + 1,
    };
    newResults[match.taskB] = {
      ...newResults[match.taskB],
      ties: newResults[match.taskB].ties + 1,
    };
    newMatch.winner = null; // Tie has no winner
  } else {
    const loser = winner === match.taskA ? match.taskB : match.taskA;
    newResults[winner] = {
      ...newResults[winner],
      wins: newResults[winner].wins + 1,
    };
    newResults[loser] = {
      ...newResults[loser],
      losses: newResults[loser].losses + 1,
    };
    newMatch.winner = winner;
  }

  const newMatches = [...state.matches];
  newMatches[state.currentMatchIndex] = newMatch;

  const historyEntry: MatchResult = {
    match: newMatch,
    round: state.currentRound,
    matchIndex: state.currentMatchIndex,
  };

  return {
    ...state,
    matches: newMatches,
    currentMatchIndex: state.currentMatchIndex + 1,
    results: newResults,
    history: [...state.history, historyEntry],
  };
}

/**
 * Undo the last match result.
 */
export function undoMatchResult(state: TournamentState): TournamentState {
  if (state.history.length === 0) return state;

  const lastResult = state.history[state.history.length - 1];
  const { match, round, matchIndex } = lastResult;

  // If we need to go back to previous round
  if (round < state.currentRound) {
    // This is complex - for now, only support undo within current round
    return state;
  }

  const newResults = { ...state.results };

  // Reverse the result
  if (match.winner === null) {
    // Was a tie
    newResults[match.taskA] = {
      ...newResults[match.taskA],
      ties: newResults[match.taskA].ties - 1,
    };
    newResults[match.taskB] = {
      ...newResults[match.taskB],
      ties: newResults[match.taskB].ties - 1,
    };
  } else {
    const winner = match.winner;
    const loser = winner === match.taskA ? match.taskB : match.taskA;
    newResults[winner] = {
      ...newResults[winner],
      wins: newResults[winner].wins - 1,
    };
    newResults[loser] = {
      ...newResults[loser],
      losses: newResults[loser].losses - 1,
    };
  }

  // Reset the match
  const newMatches = [...state.matches];
  newMatches[matchIndex] = { ...match, winner: null };

  return {
    ...state,
    matches: newMatches,
    currentMatchIndex: matchIndex,
    results: newResults,
    history: state.history.slice(0, -1),
  };
}

/**
 * Check if current round is complete.
 */
export function isRoundComplete(state: TournamentState): boolean {
  return state.currentMatchIndex >= state.matches.length;
}

/**
 * Check if tournament is complete.
 * Complete when: all rounds done, OR no more unique matchups possible.
 */
export function isTournamentComplete(state: TournamentState): boolean {
  // If we've completed all planned rounds
  if (state.currentRound > state.totalRounds) return true;
  if (isRoundComplete(state) && state.currentRound === state.totalRounds) return true;

  // Also complete if current round has no matches (all matchups exhausted)
  if (state.matches.length === 0) return true;

  return false;
}

/**
 * IMPROVEMENT 2: Check if top tasks are clearly separated
 * End early when we have enough clarity on the important tasks.
 */
function shouldEndEarly(state: TournamentState): boolean {
  // Need at least 2 rounds of data
  if (state.currentRound < 2) return false;

  const n = state.tasks.length;
  // Focus on top third or top 15, whichever is smaller
  const topCount = Math.min(15, Math.ceil(n / 3));

  const graph = buildTransitiveClosure(state.tasks, state.history);

  // Get current rankings
  const rankings = getFinalRankings(state);
  const topTasks = rankings.slice(0, topCount).map(r => r.taskId);
  const otherTasks = rankings.slice(topCount).map(r => r.taskId);

  // Check what % of relationships between top and other tasks are known
  let knownRelationships = 0;
  let totalNeeded = topTasks.length * otherTasks.length;

  if (totalNeeded === 0) return true; // All tasks are in top tier

  for (const topTask of topTasks) {
    for (const otherTask of otherTasks) {
      if (graph[topTask]?.[otherTask] || graph[otherTask]?.[topTask]) {
        knownRelationships++;
      }
    }
  }

  // End early if we have 60%+ clarity on top vs rest
  return (knownRelationships / totalNeeded) >= 0.6;
}

/**
 * Advance to the next round.
 * Uses smart matching and early termination.
 */
export function advanceToNextRound(state: TournamentState): TournamentState {
  if (!isRoundComplete(state)) return state;
  if (isTournamentComplete(state)) return state;

  // IMPROVEMENT 2: Early termination when top tasks are clear
  if (shouldEndEarly(state)) {
    return {
      ...state,
      currentRound: state.totalRounds + 1,
      matches: [],
      currentMatchIndex: 0,
    };
  }

  const nextRound = state.currentRound + 1;
  const activeTasks = state.tasks.filter(
    (id) => !state.eliminatedTasks.includes(id)
  );

  // Build transitive closure from all comparisons so far
  const graph = buildTransitiveClosure(state.tasks, state.history);

  // Generate new matches using smart tier-based pairing
  const newMatches = generateSmartMatches(activeTasks, state.results, graph, state.history);

  // If no new matches possible, tournament is done
  if (newMatches.length === 0) {
    return {
      ...state,
      currentRound: state.totalRounds + 1,
      matches: [],
      currentMatchIndex: 0,
    };
  }

  return {
    ...state,
    currentRound: nextRound,
    matches: newMatches,
    currentMatchIndex: 0,
  };
}

/**
 * Get current match for display.
 */
export function getCurrentMatch(state: TournamentState): Match | null {
  if (state.currentMatchIndex >= state.matches.length) return null;
  return state.matches[state.currentMatchIndex];
}

/**
 * Get final rankings using transitive closure for accurate ordering.
 * Falls back to win/loss record for ties.
 */
export function getFinalRankings(
  state: TournamentState
): Array<{ taskId: string; record: TournamentRecord; rank: number }> {
  const allTasks = [...state.tasks];

  // Build transitive closure for accurate comparison
  const graph = buildTransitiveClosure(state.tasks, state.history);

  // Sort using transitive relationships, with win/loss as tiebreaker
  allTasks.sort((a, b) => {
    // If we know A > B transitively, A ranks higher
    if (graph[a]?.[b]) return -1;
    if (graph[b]?.[a]) return 1;

    // Fallback to win/loss record
    const aRec = state.results[a];
    const bRec = state.results[b];

    // Primary: wins
    if (bRec.wins !== aRec.wins) return bRec.wins - aRec.wins;
    // Secondary: fewer losses
    if (aRec.losses !== bRec.losses) return aRec.losses - bRec.losses;
    // Tertiary: more ties
    return bRec.ties - aRec.ties;
  });

  return allTasks.map((taskId, index) => ({
    taskId,
    record: state.results[taskId],
    rank: index + 1,
  }));
}

/**
 * Get tournament progress info for display.
 */
export function getTournamentProgress(state: TournamentState): {
  currentRound: number;
  totalRounds: number;
  matchInRound: number;
  matchesInRound: number;
  completedMatches: number;
  remainingInRound: number;
  estimatedRemaining: number;
} {
  const completedInRound = state.currentMatchIndex;
  const totalInRound = state.matches.length;
  const remainingInRound = totalInRound - completedInRound;

  // Better estimate: with max 3 comparisons per task and early termination,
  // total matches ≈ 1.5 * n (much less than before)
  const n = state.tasks.length;
  const estimatedTotal = Math.ceil(n * 1.5);
  const estimatedRemaining = Math.max(0, estimatedTotal - state.history.length - 1);

  return {
    currentRound: state.currentRound,
    totalRounds: state.totalRounds,
    matchInRound: completedInRound + 1,
    matchesInRound: totalInRound,
    completedMatches: state.history.length,
    remainingInRound,
    estimatedRemaining: Math.min(estimatedRemaining, remainingInRound + (n / 2)),
  };
}
